import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, REVEAL, CATALOGUE, FIX, CODE, PAYOFF, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

// The 12 skill kinds — exact strings from src/lib/skills/types.ts SKILL_KINDS.
// `real` = had a working handler BEFORE the 2026-05-28 P3.A fix (only persona-rule).
const KINDS: { name: string; real: boolean; gated?: boolean }[] = [
  { name: "persona-rule", real: true },
  { name: "plan-template", real: false },
  { name: "workflow-recommendation", real: false },
  { name: "few-shot-exemplar", real: false },
  { name: "negative-exemplar", real: false },
  { name: "glossary", real: false },
  { name: "tool-preference", real: false },
  { name: "verify-rule", real: false },
  { name: "memory-query", real: false },
  { name: "cost-policy", real: false },
  { name: "recipient-change", real: false, gated: true },
  { name: "tool-allowlist-write", real: false, gated: true },
];

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const dead = interpolate(frame, [40, 72], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="self-learning agent · what broke" color={COLORS.cyan} />
          <Big>
            12 skill types shipped.
            <br />
            <span style={{ color: COLORS.red, opacity: 0.4 + dead * 0.6 }}>
              11 did NOTHING.
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="src/lib/skills/types.ts" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="cyan" title="defined · SKILL_KINDS" width={520}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 24, lineHeight: 1.6, color: COLORS.text }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
                <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 86, color: COLORS.cyan }}>12</span>
                <span style={{ color: COLORS.dim, fontSize: 22 }}>skill kinds, one array</span>
              </div>
              <div style={{ color: COLORS.dim, fontSize: 18, marginTop: 12 }}>persona-rule · glossary · tool-preference · …</div>
            </div>
          </Panel>
          <Panel accent="red" title="implemented · handlers" width={520}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 152 }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 86, color: COLORS.red }}>1</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>real handler · the rest stubbed</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — REVEAL: the no-op stub + the state='active' lie */
export const Scene03Reveal: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.reveal.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 30 }}>
      <CodePanel
        code={NOOP_CODE}
        language="tsx"
        filename="applier/handlers/no-op.ts  +  applier/index.ts"
        highlightLines={[4, 5, 11]}
        revealFrames={210}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={REVEAL} accent="amber" />
  </AbsoluteFill>
);

const NOOP_CODE = `// handlers/no-op.ts — the fallback for 11 of 12 kinds
async apply(skill, _ctx) {
  console.log(\`[no-op applier] kind=\${kind} — no target mutation\`);
  return null;                       // mutates nothing
}

// applier/index.ts — the engine, AFTER any handler returns
const priorValue = await handler.apply(skill, ctx);
await updateSkillState({
  id: skill.id,
  state: 'active',                   // flips to "active" regardless
});`;

/* 04 — CATALOGUE: the 12-kind grid, 11 inert */
export const Scene04Catalogue: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.catalogue.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 110px" }}>
        <Kicker text="12 kinds · 1 actually ran" color={COLORS.amber} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
            width: 1360,
          }}
        >
          {KINDS.map((k, i) => {
            const appear = interpolate(frame, [8 + i * 5, 22 + i * 5], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const c = k.real ? COLORS.green : k.gated ? COLORS.violet : COLORS.dim;
            const tag = k.real ? "REAL" : k.gated ? "STUB · GATED" : "STUB";
            return (
              <div
                key={k.name}
                style={{
                  opacity: appear,
                  transform: `translateY(${(1 - appear) * 12}px)`,
                  background: COLORS.panel,
                  border: `1px solid ${k.real ? COLORS.green : COLORS.line}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div style={{ fontFamily: FONTS.mono, fontSize: 19, color: k.real ? COLORS.text : COLORS.dim }}>
                  {k.name}
                </div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 13, letterSpacing: "0.14em", color: c }}>
                  {tag}
                </div>
              </div>
            );
          })}
        </div>
      </AbsoluteFill>
      <Caption lines={CATALOGUE} accent="amber" />
    </AbsoluteFill>
  );
};

/* 05 — FIX: P3.A — 12/12 real handlers via migs 209–218 */
const PIPE: PipeNode[] = [
  { label: "learned skill", value: "kind", accent: "cyan" },
  { label: "real handler", value: "apply()", sub: "P3.A", accent: "green" },
  { label: "own table", value: "mig 209–218", sub: "10 tables", accent: "lime" },
  { label: "runtime", value: "prompt / planner", sub: "real effect", accent: "amber" },
];
export const Scene05Fix: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="2026-05-28 · P3.A · the fix" color={COLORS.green} />
      <div style={{ width: 1640 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={24} nodeWidth={230} />
      </div>
      <div style={{ display: "flex", gap: 30, marginTop: 46 }}>
        <Callout label="non-gated · auto-apply" accent="green" width={420}>
          10 kinds land in their own table, then inject at runtime — with <b style={{ color: COLORS.text }}>Undo</b>.
        </Callout>
        <Callout label="gated · approval first" accent="violet" width={420}>
          <b style={{ color: COLORS.text }}>recipient-change</b> + <b style={{ color: COLORS.text }}>tool-allowlist-write</b> apply only after I approve.
        </Callout>
      </div>
    </AbsoluteFill>
    <Caption lines={FIX} accent="green" />
  </AbsoluteFill>
);

/* 06 — CODE: the registry, after the fix */
const REGISTRY = `// applier/registry.ts — after P3.A (2026-05-28)
registry.set('persona-rule', personaRuleHandler);
registry.set('tool-preference', toolPreferenceHandler);   // mig 209
registry.set('glossary', glossaryHandler);                // mig 210
registry.set('verify-rule', verifyRuleHandler);           // mig 212
registry.set('plan-template', planTemplateHandler);       // mig 214
registry.set('cost-policy', costPolicyHandler);           // mig 218
// …all 12 kinds mapped to a REAL handler now…

// the backfill loop that used to stub the rest:
for (const kind of SKILL_KINDS) {
  if (!registry.has(kind)) registry.set(kind, noOpHandler(kind));
}                            // finds nothing left — covers 0 kinds`;
export const Scene06Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={REGISTRY}
        language="tsx"
        filename="src/lib/skills/applier/registry.ts"
        highlightLines={[14, 15]}
        revealFrames={300}
        fontSize={23}
        width={1300}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 07 — PAYOFF: 1/12 → 12/12 */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="handlers that actually run" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 66, color: COLORS.dim }}>1 / 12</span>
          <span style={{ fontFamily: FONTS.display, fontSize: 58, color: COLORS.dim }}>→</span>
          <MetricCounter value={12} from={1} startFrame={10} durationFrames={56} accent="lime" suffix=" / 12" fontSize={140} />
        </div>
        <div style={{ opacity: c1, marginTop: 40 }}>
          <Callout label="docs/2026-05-28-skills-lifecycle-p3a-complete.md" accent="green" width={760}>
            <b style={{ color: COLORS.text }}>216 / 216</b> jest green · the no-op stub still exists in the repo — but it's reachable by <b style={{ color: COLORS.text }}>nothing</b>.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          state = &apos;active&apos;
          <br />
          is not the same as
          <br />
          <span style={{ color: COLORS.cyan }}>it did something.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
